import { Modal } from 'antd'
import { BaseButton } from 'components/elements/BaseButton'
import { queryClient } from 'lib/react-query'
import { FC, useEffect, useState } from 'react'
import { useGetCallStatusQuery, useInitiateCallMutation } from '../api'
import { TCallStatus } from '../types'

const TERMINAL_STATUSES: TCallStatus[] = [
 'completed',
 'no-answer',
 'busy',
 'failed',
]

// Twilio needs E.164 (e.g. +919562191932). The lead's raw `number` field is
// bare local digits with no country code (e.g. "6473670609") - the country
// code only shows up in the backend-supplied `phone` field used for display
// (e.g. "1 (647) 367-0609"). Prefer that; fall back to assuming North America
// (+1) for a bare 10-digit `number`, matching this app's default elsewhere
// (new leads are always created with country_code: 1).
const toE164 = (raw?: string): string | undefined => {
 if (!raw) return undefined
 const digits = raw.replace(/\D/g, '')
 if (!digits) return undefined
 const withCountryCode = digits.length === 10 ? `1${digits}` : digits
 return `+${withCountryCode}`
}

const CallingStatusModal: FC<{
 leadId?: any
 number?: string
 displayPhone?: string
 leadName?: string
 onClose?: () => void
}> = ({ leadId, number, displayPhone, leadName, onClose }) => {
 const [callId, setCallId] = useState<number>()
 const [initError, setInitError] = useState<string>()
 const [initiateCall, { isLoading: initiating }] = useInitiateCallMutation()

 const { data: statusData } = useGetCallStatusQuery(
  { id: callId as number },
  {
   skip: !callId,
   pollingInterval: 2500,
  },
 )

 const status: TCallStatus = statusData?.status ?? 'initiated'
 const isTerminal = TERMINAL_STATUSES.includes(status)
 const hasFailed = !!initError || (isTerminal && status !== 'completed')
 const isConnected = !hasFailed && status === 'answered'
 const e164Number = toE164(displayPhone) ?? toE164(number)
 const contactLabel = displayPhone ? `+${displayPhone}` : e164Number

 const startCall = async () => {
  setInitError(undefined)
  setCallId(undefined)
  try {
   const res = await initiateCall({
    lead_id: leadId,
    number: e164Number,
   }).unwrap()
   setCallId(res?.id)
  } catch (err: any) {
   setInitError(err?.data?.message ?? 'Couldn’t reach your phone — try again')
  }
 }

 useEffect(() => {
  startCall()
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [])

 useEffect(() => {
  if (status === 'completed') {
   queryClient.invalidateQueries(`leads/${leadId}/calls`)
   queryClient.invalidateQueries('calls')
   onClose?.()
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [status])

 const title = () => {
  if (hasFailed) return 'Couldn’t connect'
  if (status === 'answered') return 'Connected…'
  return 'Calling your phone...'
 }

 const subtitle = () => {
  if (hasFailed) return initError ?? 'Couldn’t reach your phone — try again'
  if (status === 'answered') return `Bridging to ${leadName ?? 'the lead'}`
  return 'Answer your phone to be connected with the lead'
 }

 return (
  <Modal
   footer={null}
   visible
   closable={false}
   centered
   width={340}
   wrapClassName="calling-status-modal"
  >
   <div className="calling-modal-body">
    <div
     className={`calling-modal-icon-wrap ${
      hasFailed ? 'is-error' : isConnected ? 'is-connected' : ''
     }`}
    >
     {!hasFailed && (
      <>
       <span className="calling-modal-pulse" />
       <span className="calling-modal-pulse calling-modal-pulse-delay" />
      </>
     )}
     <div className="calling-modal-icon">
      <i
       className={`fas ${
        hasFailed ? 'fa-phone-slash fa-flip-horizontal' : 'fa-phone-alt'
       }`}
      />
     </div>
    </div>
    <h3
     className={`calling-modal-title ${
      hasFailed ? 'is-error' : isConnected ? 'is-connected' : ''
     }`}
    >
     {title()}
    </h3>
    <p className="calling-modal-subtitle">{subtitle()}</p>
    {contactLabel && (
     <div className="calling-modal-contact">
      <i className="fas fa-user" />
      <span>{leadName ? `${leadName} - ${contactLabel}` : contactLabel}</span>
     </div>
    )}
    {hasFailed ? (
     <div className="calling-modal-actions">
      <BaseButton
       block
       loading={initiating}
       onClick={startCall}
       customClass="calling-modal-btn calling-modal-btn-primary"
      >
       Retry
      </BaseButton>
      <BaseButton
       block
       onClick={onClose}
       customClass="calling-modal-btn calling-modal-btn-secondary"
      >
       Hide
      </BaseButton>
     </div>
    ) : (
     <BaseButton
      block
      onClick={onClose}
      customClass={`calling-modal-btn ${
       isConnected ? 'calling-modal-btn-success' : 'calling-modal-btn-primary'
      }`}
     >
      Hide
     </BaseButton>
    )}
   </div>
  </Modal>
 )
}

export default CallingStatusModal
